import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { AlertTriangle, Radio, Anchor, MapPin, Target, Square, MousePointer2, Trash2, X, Locate } from "lucide-react";
import { addNoaaLayer, setNoaaVisibility, firstSymbolLayerId } from "@/lib/noaaLayer";

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
if (TOKEN) {
    mapboxgl.accessToken = TOKEN;
}

/**
 * TrialMap
 * -----------------------------------------------------------------------------
 * Interactive mission planner for the Trial editor pre-test tab.
 *
 * MODES:
 *   - select     : default. Click markers to select+edit; drag to move.
 *   - gcs        : next click drops a "Ground Control Station" marker.
 *   - launch     : next click drops the primary launch site.
 *   - alt_launch : next click drops the alternate launch site.
 *   - waypoint   : each click drops a numbered waypoint (persists so operator
 *                  can drop multiples without re-selecting the tool).
 *   - test_area  : first click sets NW corner, second click sets SE corner
 *                  of a rectangular test-area overlay.
 *
 * Locations shape (persisted in `trial.locations[]`):
 *   { id, kind, name, lat, lng, notes }
 * or for kind === "test_area_box":
 *   { id, kind, name, nw_lat, nw_lng, se_lat, se_lng }
 */

const KIND_META = {
    gcs:        { label: "GCS",        color: "#22d3ee", icon: Radio,   defaultName: "Ground Control" },
    launch:     { label: "Launch",     color: "#FF5500", icon: Anchor,  defaultName: "Launch site" },
    alt_launch: { label: "Alt Launch", color: "#eab308", icon: Anchor,  defaultName: "Alternate launch" },
    waypoint:   { label: "Waypoint",   color: "#ffffff", icon: MapPin,  defaultName: "Waypoint" },
    objective:  { label: "Objective",  color: "#22c55e", icon: Target,  defaultName: "Objective" },
};

const isNum = (n) => Number.isFinite(n);
const uid = () => Math.random().toString(36).slice(2, 10);

export default function TrialMap({ locations = [], onChange, primarySite, altSite }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]); // {loc_id, marker}
    const boxSourceAdded = useRef(false);
    const roRef = useRef(null);
    const [mapReady, setMapReady] = useState(0);
    const [mode, setMode] = useState("select");
    const [selectedId, setSelectedId] = useState(null);
    const [pendingNW, setPendingNW] = useState(null); // {lat,lng} while drawing box
    // NOAA nautical chart overlay — default OFF for Trials (planning surface).
    const [noaaOn, setNoaaOn] = useState(false);

    // Keep the latest onChange in a ref so the mapbox click handler (which is
    // wired once) always sees fresh state.
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const locationsRef = useRef(locations);
    locationsRef.current = locations;
    const modeRef = useRef(mode);
    modeRef.current = mode;
    const pendingNWRef = useRef(pendingNW);
    pendingNWRef.current = pendingNW;

    // ---- Map init ------------------------------------------------------------
    useEffect(() => {
        if (!TOKEN || !containerRef.current || mapRef.current) return;
        let cancelled = false;
        const init = () => {
            if (cancelled || mapRef.current || !containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            if (rect.height < 40) {
                requestAnimationFrame(init);
                return;
            }
            // Center on any existing point, else a neutral US East coast center.
            const seedPoint = locationsRef.current.find((l) => isNum(l.lat) && isNum(l.lng));
            const center = seedPoint ? [seedPoint.lng, seedPoint.lat] : [-76.85, 34.95];
            const map = new mapboxgl.Map({
                container: containerRef.current,
                style: "mapbox://styles/mapbox/dark-v11",
                center,
                zoom: seedPoint ? 12 : 7,
                attributionControl: false,
                projection: "mercator",
            });
            map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
            map.addControl(new mapboxgl.ScaleControl({ maxWidth: 120, unit: "imperial" }), "bottom-right");
            mapRef.current = map;

            map.on("load", () => {
                map.addSource("trial-box", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
                map.addLayer({
                    id: "trial-box-fill",
                    type: "fill",
                    source: "trial-box",
                    paint: { "fill-color": "#FF5500", "fill-opacity": 0.14 },
                });
                map.addLayer({
                    id: "trial-box-outline",
                    type: "line",
                    source: "trial-box",
                    paint: { "line-color": "#FF5500", "line-width": 2, "line-dasharray": [2, 2] },
                });
                boxSourceAdded.current = true;
                // NOAA raster overlay — inserted BELOW the trial-box-fill/outline
                // so pins + rectangle stay visually on top. Default OFF; the
                // toolbar toggle flips visibility without re-fetching tiles.
                addNoaaLayer(map, {
                    visible: false,
                    opacity: 0.85,
                    beforeLayerId: firstSymbolLayerId(map),
                });
                setMapReady((n) => n + 1);
            });

            // Click handler: routes based on current mode.
            map.on("click", (e) => {
                const m = modeRef.current;
                if (m === "select") return;
                const { lng, lat } = e.lngLat;
                if (m === "test_area") {
                    handleBoxClick(lat, lng);
                } else if (KIND_META[m]) {
                    addPoint(m, lat, lng);
                }
            });

            // Cursor feedback per mode.
            const canvas = map.getCanvas();
            canvas.style.cursor = "";

            const safeResize = () => {
                try { mapRef.current?.resize(); } catch { /* ignore */ }
            };
            const ro = new ResizeObserver(safeResize);
            ro.observe(containerRef.current);
            roRef.current = ro;
        };
        requestAnimationFrame(init);

        return () => {
            cancelled = true;
            roRef.current?.disconnect();
            roRef.current = null;
            markersRef.current.forEach(({ marker }) => marker.remove());
            markersRef.current = [];
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update cursor whenever mode changes.
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        const canvas = map.getCanvas();
        canvas.style.cursor = mode === "select" ? "" : "crosshair";
    }, [mode]);

    // Flip NOAA overlay visibility whenever the toggle changes (or the map becomes ready).
    useEffect(() => {
        setNoaaVisibility(mapRef.current, noaaOn);
    }, [noaaOn, mapReady]);

    // ---- Marker sync ---------------------------------------------------------
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        // Wipe stale markers.
        markersRef.current.forEach(({ marker }) => marker.remove());
        markersRef.current = [];

        // Waypoints get numbered by their order in the locations array.
        let wpNum = 0;
        locations.forEach((loc) => {
            if (loc.kind === "test_area_box") return;
            if (!isNum(loc.lat) || !isNum(loc.lng)) return;
            const meta = KIND_META[loc.kind] || KIND_META.waypoint;
            if (loc.kind === "waypoint") wpNum += 1;
            const label = loc.kind === "waypoint"
                ? `WP${wpNum}`
                : (loc.name || meta.label);
            const el = buildPinElement(meta.color, label, loc.id === selectedId, loc.id);
            el.addEventListener("click", (ev) => {
                ev.stopPropagation();
                setSelectedId(loc.id);
            });
            const marker = new mapboxgl.Marker({ element: el, anchor: "bottom", draggable: true })
                .setLngLat([loc.lng, loc.lat])
                .addTo(map);
            marker.on("dragend", () => {
                const p = marker.getLngLat();
                updateLocation(loc.id, { lat: p.lat, lng: p.lng });
            });
            markersRef.current.push({ loc_id: loc.id, marker });
        });

        // Update rectangle overlay.
        if (boxSourceAdded.current) {
            const box = locations.find((l) => l.kind === "test_area_box");
            const src = map.getSource("trial-box");
            if (box && isNum(box.nw_lat) && isNum(box.nw_lng) && isNum(box.se_lat) && isNum(box.se_lng)) {
                const coords = [[
                    [box.nw_lng, box.nw_lat],
                    [box.se_lng, box.nw_lat],
                    [box.se_lng, box.se_lat],
                    [box.nw_lng, box.se_lat],
                    [box.nw_lng, box.nw_lat],
                ]];
                src?.setData({
                    type: "FeatureCollection",
                    features: [{ type: "Feature", geometry: { type: "Polygon", coordinates: coords }, properties: {} }],
                });
            } else {
                src?.setData({ type: "FeatureCollection", features: [] });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locations, mapReady, selectedId]);

    // ---- Data mutators (call parent onChange with new locations array) ------

    const addPoint = useCallback((kind, lat, lng) => {
        const meta = KIND_META[kind];
        const existing = locationsRef.current || [];
        // Only ONE of gcs / launch / alt_launch may exist; new one replaces.
        const unique = kind === "gcs" || kind === "launch" || kind === "alt_launch";
        let next = unique
            ? existing.filter((l) => l.kind !== kind)
            : [...existing];
        const loc = {
            id: uid(),
            kind,
            name: meta?.defaultName || kind,
            lat, lng,
            notes: "",
        };
        next = [...next, loc];
        onChangeRef.current?.(next);
        setSelectedId(loc.id);
        // After a "one-off" placement, snap back to select mode; waypoints stay in mode.
        if (kind !== "waypoint") setMode("select");
    }, []);

    const handleBoxClick = useCallback((lat, lng) => {
        const pending = pendingNWRef.current;
        if (!pending) {
            setPendingNW({ lat, lng });
            return;
        }
        // Compute NW / SE regardless of click order.
        const nw_lat = Math.max(pending.lat, lat);
        const se_lat = Math.min(pending.lat, lat);
        const nw_lng = Math.min(pending.lng, lng);
        const se_lng = Math.max(pending.lng, lng);
        const existing = (locationsRef.current || []).filter((l) => l.kind !== "test_area_box");
        const box = {
            id: uid(),
            kind: "test_area_box",
            name: "Test area",
            nw_lat, nw_lng, se_lat, se_lng,
        };
        onChangeRef.current?.([...existing, box]);
        setPendingNW(null);
        setMode("select");
    }, []);

    const updateLocation = useCallback((locId, patch) => {
        const existing = locationsRef.current || [];
        const next = existing.map((l) => (l.id === locId ? { ...l, ...patch } : l));
        onChangeRef.current?.(next);
    }, []);

    const removeLocation = useCallback((locId) => {
        const existing = locationsRef.current || [];
        onChangeRef.current?.(existing.filter((l) => l.id !== locId));
        setSelectedId((s) => (s === locId ? null : s));
    }, []);

    const clearAll = useCallback(() => {
        if (!window.confirm("Clear all map markers and the test area?")) return;
        onChangeRef.current?.([]);
        setSelectedId(null);
        setPendingNW(null);
        setMode("select");
    }, []);

    const flyToLocation = useCallback((loc) => {
        const map = mapRef.current;
        if (!map) return;
        if (loc.kind === "test_area_box") {
            const cLat = (loc.nw_lat + loc.se_lat) / 2;
            const cLng = (loc.nw_lng + loc.se_lng) / 2;
            map.flyTo({ center: [cLng, cLat], zoom: 14, duration: 700 });
        } else {
            map.flyTo({ center: [loc.lng, loc.lat], zoom: 15, duration: 700 });
        }
    }, []);

    // Derived: sorted points (excluding box) for the sidebar list.
    const listItems = useMemo(() => {
        const pts = locations.filter((l) => l.kind !== "test_area_box");
        const box = locations.find((l) => l.kind === "test_area_box");
        return { pts, box };
    }, [locations]);

    if (!TOKEN) {
        return (
            <div className="border border-dashed border-virtus-line h-[420px] flex items-center justify-center" data-testid="trial-map-missing-token">
                <div className="max-w-md text-center px-6">
                    <AlertTriangle size={40} className="mx-auto text-virtus-orange mb-4" />
                    <div className="font-heading text-lg uppercase tracking-tight text-white mb-2">Mapbox token missing</div>
                    <div className="text-sm text-virtus-dim">Add <span className="text-virtus-orange font-mono">REACT_APP_MAPBOX_TOKEN</span> to <span className="font-mono">frontend/.env</span> and restart the frontend.</div>
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-3" data-testid="trial-map">
            <div className="relative border border-virtus-line" style={{ minHeight: 440, height: 440 }}>
                <div ref={containerRef} style={{ position: "absolute", inset: 0 }} />

                {/* Toolbar overlay */}
                <div className="absolute top-2 left-2 z-10 flex flex-wrap gap-1 bg-black/85 border border-virtus-line p-1 backdrop-blur">
                    <ToolBtn mode={mode} value="select"     onClick={setMode} icon={MousePointer2} label="SELECT"  testid="map-tool-select" />
                    <ToolBtn mode={mode} value="gcs"        onClick={setMode} icon={Radio}          label="GCS"     testid="map-tool-gcs" color={KIND_META.gcs.color} />
                    <ToolBtn mode={mode} value="launch"     onClick={setMode} icon={Anchor}         label="LAUNCH"  testid="map-tool-launch" color={KIND_META.launch.color} />
                    <ToolBtn mode={mode} value="alt_launch" onClick={setMode} icon={Anchor}         label="ALT"     testid="map-tool-alt-launch" color={KIND_META.alt_launch.color} />
                    <ToolBtn mode={mode} value="waypoint"   onClick={setMode} icon={MapPin}         label="WAYPT"   testid="map-tool-waypoint" color={KIND_META.waypoint.color} />
                    <ToolBtn mode={mode} value="test_area"  onClick={(v) => { setMode(v); setPendingNW(null); }} icon={Square} label="BOX" testid="map-tool-test-area" color="#FF5500" />
                    <button
                        onClick={clearAll}
                        title="Clear all"
                        data-testid="map-tool-clear"
                        className="flex items-center gap-1 font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-dim hover:text-virtus-stuck border border-virtus-line px-2 py-1"
                    >
                        <Trash2 size={10} /> CLEAR
                    </button>
                    <div className="w-px bg-virtus-line mx-1" />
                    <button
                        onClick={() => setNoaaOn((v) => !v)}
                        title={noaaOn ? "Hide seamark overlay" : "Show seamark overlay (buoys, lights, harbor limits)"}
                        data-testid="map-tool-noaa"
                        className={`flex items-center gap-1 font-mono text-[9px] tracking-[0.24em] uppercase px-2 py-1 border transition-colors ${
                            noaaOn
                                ? "bg-virtus-orange text-black border-virtus-orange"
                                : "text-virtus-dim hover:text-white border-virtus-line hover:border-virtus-orange"
                        }`}
                    >
                        <Anchor size={10} /> SEAMARKS
                    </button>
                </div>

                {/* Mode hint */}
                {mode !== "select" && (
                    <div className="absolute bottom-3 left-3 z-10 bg-black/85 border border-virtus-orange px-3 py-1.5 backdrop-blur">
                        <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-orange">
                            {mode === "test_area"
                                ? (pendingNW ? "// CLICK SECOND CORNER" : "// CLICK FIRST CORNER")
                                : `// CLICK MAP TO PLACE ${(KIND_META[mode]?.label || mode).toUpperCase()}`}
                        </div>
                    </div>
                )}

                {/* Site hints (bidirectional context — never overwrites the text) */}
                {(primarySite || altSite) && (
                    <div className="absolute top-2 right-14 z-10 max-w-[220px] bg-black/70 border border-virtus-line px-2 py-1 backdrop-blur pointer-events-none">
                        <div className="font-mono text-[8px] tracking-[0.24em] uppercase text-virtus-dim leading-tight">
                            {primarySite && <div>P · {primarySite}</div>}
                            {altSite && <div>A · {altSite}</div>}
                        </div>
                    </div>
                )}
            </div>

            {/* Sidebar list */}
            <div className="border border-virtus-line bg-virtus-bg p-2 md:p-3 space-y-2 max-h-[440px] overflow-y-auto" data-kiosk-scroll="false">
                <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-dim border-b border-virtus-line pb-1">
                    // NAMED LOCATIONS
                </div>
                {listItems.pts.length === 0 && !listItems.box && (
                    <div className="text-[11px] text-virtus-faint py-2 leading-relaxed">
                        Pick a tool above and click the map to drop a marker.
                    </div>
                )}
                {listItems.pts.map((loc, i) => {
                    const meta = KIND_META[loc.kind] || KIND_META.waypoint;
                    const Icon = meta.icon;
                    const wpNum = loc.kind === "waypoint"
                        ? listItems.pts.filter((l, j) => l.kind === "waypoint" && j <= i).length
                        : null;
                    const selected = loc.id === selectedId;
                    return (
                        <div key={loc.id}
                             className={`border ${selected ? "border-virtus-orange" : "border-virtus-line"} bg-virtus-surface p-2`}
                             data-testid={`map-location-row-${loc.id}`}>
                            <div className="flex items-center gap-1.5 mb-1">
                                <Icon size={10} style={{ color: meta.color }} />
                                <span className="font-mono text-[9px] tracking-[0.24em] uppercase" style={{ color: meta.color }}>
                                    {meta.label}{wpNum ? ` ${wpNum}` : ""}
                                </span>
                                <div className="flex-1" />
                                <button onClick={() => flyToLocation(loc)} title="Fly to"
                                        className="text-virtus-dim hover:text-virtus-orange">
                                    <Locate size={11} />
                                </button>
                                <button onClick={() => removeLocation(loc.id)} title="Remove"
                                        className="text-virtus-dim hover:text-virtus-stuck"
                                        data-testid={`map-location-remove-${loc.id}`}>
                                    <X size={11} />
                                </button>
                            </div>
                            <input
                                value={loc.name || ""}
                                onChange={(e) => updateLocation(loc.id, { name: e.target.value })}
                                placeholder="Name"
                                onFocus={() => setSelectedId(loc.id)}
                                className="w-full bg-transparent border-0 border-b border-virtus-line focus:border-virtus-orange px-0 py-0.5 text-white outline-none text-xs mb-1"
                                data-testid={`map-location-name-${loc.id}`}
                            />
                            <div className="font-mono text-[9px] text-virtus-faint tracking-wider">
                                {loc.lat?.toFixed(5)}, {loc.lng?.toFixed(5)}
                            </div>
                        </div>
                    );
                })}

                {listItems.box && (
                    <div className={`border ${selectedId === listItems.box.id ? "border-virtus-orange" : "border-virtus-line"} bg-virtus-surface p-2`}
                         data-testid={`map-location-row-${listItems.box.id}`}>
                        <div className="flex items-center gap-1.5 mb-1">
                            <Square size={10} className="text-virtus-orange" />
                            <span className="font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-orange">TEST AREA</span>
                            <div className="flex-1" />
                            <button onClick={() => flyToLocation(listItems.box)} title="Fly to"
                                    className="text-virtus-dim hover:text-virtus-orange">
                                <Locate size={11} />
                            </button>
                            <button onClick={() => removeLocation(listItems.box.id)} title="Remove"
                                    className="text-virtus-dim hover:text-virtus-stuck"
                                    data-testid={`map-location-remove-${listItems.box.id}`}>
                                <X size={11} />
                            </button>
                        </div>
                        <input
                            value={listItems.box.name || ""}
                            onChange={(e) => updateLocation(listItems.box.id, { name: e.target.value })}
                            placeholder="Test area label"
                            className="w-full bg-transparent border-0 border-b border-virtus-line focus:border-virtus-orange px-0 py-0.5 text-white outline-none text-xs mb-1"
                            data-testid={`map-location-name-${listItems.box.id}`}
                        />
                        <div className="font-mono text-[9px] text-virtus-faint tracking-wider">
                            NW {listItems.box.nw_lat?.toFixed(4)}, {listItems.box.nw_lng?.toFixed(4)}<br />
                            SE {listItems.box.se_lat?.toFixed(4)}, {listItems.box.se_lng?.toFixed(4)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ToolBtn({ mode, value, onClick, icon: Icon, label, testid, color }) {
    const active = mode === value;
    return (
        <button
            onClick={() => onClick(value)}
            data-testid={testid}
            className={`flex items-center gap-1 font-mono text-[9px] tracking-[0.24em] uppercase px-2 py-1 border transition-colors ${
                active
                    ? "bg-virtus-orange text-black border-virtus-orange"
                    : "text-virtus-dim hover:text-white border-virtus-line hover:border-virtus-orange"
            }`}
        >
            <Icon size={10} style={color && !active ? { color } : undefined} />
            {label}
        </button>
    );
}

// Build a DOM element for a marker pin.
function buildPinElement(color, label, selected, dataId) {
    const wrap = document.createElement("div");
    wrap.style.position = "relative";
    wrap.style.width = "22px";
    wrap.style.height = "30px";
    wrap.style.cursor = "pointer";
    wrap.setAttribute("data-testid", `trial-map-pin-${dataId}`);

    // Teardrop-like pin: circle head + tail
    const head = document.createElement("div");
    head.style.width = "16px";
    head.style.height = "16px";
    head.style.borderRadius = "50%";
    head.style.background = color;
    head.style.border = `2px solid ${selected ? "#FFFFFF" : "rgba(5,5,5,0.9)"}`;
    head.style.boxShadow = selected ? `0 0 0 3px ${color}55` : `0 0 6px ${color}88`;
    head.style.position = "absolute";
    head.style.top = "0";
    head.style.left = "3px";
    wrap.appendChild(head);

    // Tail
    const tail = document.createElement("div");
    tail.style.position = "absolute";
    tail.style.left = "8px";
    tail.style.top = "12px";
    tail.style.width = "0";
    tail.style.height = "0";
    tail.style.borderLeft = "3px solid transparent";
    tail.style.borderRight = "3px solid transparent";
    tail.style.borderTop = `18px solid ${color}`;
    wrap.appendChild(tail);

    // Label
    const lbl = document.createElement("div");
    lbl.textContent = label;
    lbl.style.position = "absolute";
    lbl.style.top = "-14px";
    lbl.style.left = "50%";
    lbl.style.transform = "translateX(-50%)";
    lbl.style.fontFamily = "'JetBrains Mono', monospace";
    lbl.style.fontSize = "9px";
    lbl.style.letterSpacing = "0.16em";
    lbl.style.color = "#FFFFFF";
    lbl.style.background = "rgba(5,5,5,0.85)";
    lbl.style.border = `1px solid ${color}`;
    lbl.style.padding = "1px 4px";
    lbl.style.whiteSpace = "nowrap";
    lbl.style.pointerEvents = "none";
    wrap.appendChild(lbl);

    return wrap;
}
